import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import api from '../services/api'
import i18n from '../i18n'

export const useWorkspaceStore = defineStore('workspace', () => {
  const workspaces = ref([])
  const currentWorkspace = ref(null)
  const isLoading = ref(false)
  const toast = useToast()

  const getWorkspaces = async () => {
    try {
      isLoading.value = true
      // Evita chamadas sem token
      const token = localStorage.getItem('token')
      if (!token) {
        workspaces.value = []
        return { success: false, error: 'Sem token' }
      }
      const response = await api.get('/workspaces')
      workspaces.value = response.data
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao buscar workspaces'
      toast.error(message)
      return { success: false, error: message }
    } finally {
      isLoading.value = false
    }
  }

  const getUserWorkspaces = async () => {
    try {
      isLoading.value = true
      console.log('🔄 WorkspaceStore: Iniciando busca de workspaces...')
      
      // Evita chamadas sem token
      const token = localStorage.getItem('token')
      if (!token) {
        console.log('🔒 WorkspaceStore: Sem token, não buscando workspaces')
        workspaces.value = []
        return []
      }
      
      console.log('🌐 WorkspaceStore: Fazendo chamada para /workspaces...')
      const response = await api.get('/workspaces')
      console.log('📦 WorkspaceStore: Resposta recebida:', response.data)
      
      workspaces.value = response.data || []
      console.log('✅ WorkspaceStore: Workspaces carregados:', workspaces.value)
      return response.data || []
    } catch (error) {
      console.error('❌ WorkspaceStore: Erro ao buscar workspaces do usuário:', error)
      workspaces.value = []
      return []
    } finally {
      isLoading.value = false
    }
  }

  const createWorkspace = async (workspaceData) => {
    try {
      isLoading.value = true
      console.log('🔄 Criando workspace:', workspaceData)
      
      const response = await api.post('/workspaces', workspaceData)
      console.log('✅ Workspace criado no backend:', response.data)
      
      // 🔄 Recarregar TODOS os workspaces do backend para pegar a ordenação correta
      console.log('🔄 Recarregando workspaces para sincronizar ordenação...')
      await getWorkspaces()
      console.log('✅ Workspaces recarregados com ordenação correta do backend')
      
      // Mostrar toast de sucesso
      toast.success(i18n.global.t('workspaceStore.toast.created', { name: response.data.nome }))
      
      return { success: true, workspace: response.data }
    } catch (error) {
      console.error('❌ Erro ao criar workspace:', error)
      const message = error.response?.data?.error || 'Erro ao criar workspace'
      toast.error(message)
      return { success: false, error: message }
    } finally {
      isLoading.value = false
    }
  }

  const updateWorkspace = async (id, workspaceData) => {
    try {
      isLoading.value = true
      console.log('🔄 Atualizando workspace:', id, workspaceData)
      
      const response = await api.put(`/workspaces/${id}`, workspaceData)
      console.log('✅ Workspace atualizado no backend:', response.data)
      
      // Atualizar na lista local
      const index = workspaces.value.findIndex(w => w.id === id)
      if (index !== -1) {
        workspaces.value[index] = response.data
        console.log('📊 Lista de workspaces atualizada')
      }
      
      // Atualizar workspace atual se for o mesmo
      if (currentWorkspace.value?.id === id) {
        currentWorkspace.value = response.data
        console.log('🎯 Workspace atual atualizado')
      }
      
      toast.success(i18n.global.t('workspaceStore.toast.updated', { name: response.data.nome }))
      return { success: true, workspace: response.data }
    } catch (error) {
      console.error('❌ Erro ao atualizar workspace:', error)
      const message = error.response?.data?.error || 'Erro ao atualizar workspace'
      toast.error(message)
      return { success: false, error: message }
    } finally {
      isLoading.value = false
    }
  }

  const deleteWorkspace = async (id) => {
    try {
      isLoading.value = true
      console.log('[STORE] Deletando workspace ID:', id)
      
      await api.delete(`/workspaces/${id}`)
      console.log('[STORE] Workspace deletado no backend')
      
      workspaces.value = workspaces.value.filter(w => w.id !== id)
      console.log('[STORE] Workspace removido da lista do store')
      
      if (currentWorkspace.value?.id === id) {
        currentWorkspace.value = null
      }
      
      toast.success(i18n.global.t('workspaceStore.toast.deleted'))
      console.log('[STORE] Retornando success: true')
      return { success: true }
    } catch (error) {
      console.error('[STORE] Erro ao deletar:', error)
      const message = error.response?.data?.error || 'Erro ao deletar workspace'
      toast.error(message)
      return { success: false, error: message }
    } finally {
      isLoading.value = false
    }
  }

  const getWorkspaceById = async (id) => {
    try {
      isLoading.value = true
      const response = await api.get(`/workspaces/${id}`)
      currentWorkspace.value = response.data
      return { success: true, workspace: response.data }
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao buscar workspace'
      toast.error(message)
      return { success: false, error: message }
    } finally {
      isLoading.value = false
    }
  }

  const searchWorkspaces = async (term) => {
    try {
      const response = await api.get(`/workspaces/search?q=${encodeURIComponent(term)}`)
      return { success: true, workspaces: response.data }
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao buscar workspaces'
      return { success: false, error: message }
    }
  }

  const setCurrentWorkspace = (workspace) => {
    currentWorkspace.value = workspace
  }

  const clearCurrentWorkspace = () => {
    currentWorkspace.value = null
  }

  const clearSession = () => {
    workspaces.value = []
    currentWorkspace.value = null
    isLoading.value = false
  }

  const getWorkspacesByType = (type) => {
    return computed(() => workspaces.value.filter(w => w.tipo === type))
  }

  const reorderWorkspaces = async (newOrder) => {
    try {
      // Atualizar ordem local
      workspaces.value = newOrder
      
      // Salvar ordem no backend (se necessário)
      const orderData = newOrder.map((workspace, index) => ({
        id: workspace.id,
        ordem: index
      }))
      
      // Aqui você pode fazer uma chamada para o backend para salvar a ordem
      // await api.put('/workspaces/reorder', { order: orderData })
      
      return { success: true }
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao reordenar workspaces'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  const getWorkspaceOrder = (userId) => {
    try {
      const savedOrder = localStorage.getItem(`workspace-order-${userId}`)
      return savedOrder ? JSON.parse(savedOrder) : null
    } catch (error) {
      console.error('Erro ao carregar ordem dos workspaces:', error)
      return null
    }
  }

  const saveWorkspaceOrder = (userId, order) => {
    try {
      localStorage.setItem(`workspace-order-${userId}`, JSON.stringify(order))
      return { success: true }
    } catch (error) {
      console.error('Erro ao salvar ordem dos workspaces:', error)
      return { success: false, error: error.message }
    }
  }

  const syncWorkspaces = async () => {
    try {
      console.log('🔄 Sincronizando workspaces...')
      const response = await api.get('/workspaces')
      workspaces.value = response.data || []
      console.log('✅ Workspaces sincronizados:', workspaces.value.length)
      return { success: true }
    } catch (error) {
      console.error('❌ Erro ao sincronizar workspaces:', error)
      return { success: false, error: error.message }
    }
  }

  const loadWorkspace = async (workspaceId) => {
    try {
      console.log('🔄 Carregando workspace:', workspaceId)
      
      // Buscar workspace específico
      const response = await api.get(`/workspaces/${workspaceId}`)
      currentWorkspace.value = response.data
      
      console.log('✅ Workspace carregado:', currentWorkspace.value)
      return { success: true, workspace: response.data }
    } catch (error) {
      console.error('❌ Erro ao carregar workspace:', error)
      
      // Se for 401 e não houver token, o usuário deslogou - não mostrar erro
      const isUnauthorized = error.response?.status === 401
      const hasToken = !!localStorage.getItem('token')
      
      if (isUnauthorized && !hasToken) {
        console.log('[WORKSPACE] ℹ️ Usuário deslogado, ignorando erro')
        return { success: false, error: 'Não autorizado' }
      }
      
      const message = error.response?.data?.error || 'Erro ao carregar workspace'
      toast.error(message)
      return { success: false, error: message }
    }
  }

  return {
    workspaces,
    currentWorkspace,
    isLoading,
    getWorkspaces,
    getUserWorkspaces,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    getWorkspaceById,
    searchWorkspaces,
    setCurrentWorkspace,
    clearCurrentWorkspace,
    clearSession,
    getWorkspacesByType,
    reorderWorkspaces,
    getWorkspaceOrder,
    saveWorkspaceOrder,
    syncWorkspaces,
    loadWorkspace
  }
})
